// src/auth/ResetPassword.js
import React, { useState, useEffect } from "react";
import usePageMeta from "../hooks/usePageMeta";
import { useParams, useNavigate, Link } from "react-router-dom";
import API from "../api";
import { showError, showSuccess } from "../utils/dialog";
import "../App.css";
import talentLogo2 from "../assets/talentlogo2.png";

function ResetPassword() {
  usePageMeta("Reset Password");
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenMessage, setTokenMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setTokenMessage("Invalid reset token.");
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const res = await API.get(`/auth/reset-password/${token}`);
        setTokenValid(res.data.valid);
        if (!res.data.valid) {
          setTokenMessage(res.data.message || "Invalid or expired token.");
        }
      } catch (err) {
        setTokenValid(false);
        setTokenMessage(err.response?.data?.message || "Invalid or expired reset token.");
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.trim() === "") {
      showError("Password is required");
      return;
    }
    if (password.length < 8) {
      showError("Password must be at least 8 characters long");
      return;
    }
    if (!token) {
      showError("Invalid reset token");
      return;
    }
    try {
      const res = await API.post(`/auth/reset-password/${token}`, { password });
      showSuccess(res.data.message || "Password reset successful");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || "Something went wrong";
      showError(msg);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="auth-container">
        <div className="background-overlay"></div>
        <div className="form-container">
          <div className="form-card">
            <div className="atlas-logo">
              <img src={talentLogo2} alt="Talent Logo" className="h-14 w-auto" />
            </div>
            <h1 className="form-title">Set New Password</h1>
            <p style={{ textAlign: "center", fontSize: "0.875rem", color: "#6b7280" }}>
              Validating your reset link...
            </p>
            <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
              <div className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (tokenValid === false) {
    return (
      <div className="auth-container">
        <div className="background-overlay"></div>
        <div className="form-container">
          <div className="form-card">
            <div className="atlas-logo">
              <img src={talentLogo2} alt="Talent Logo" className="h-14 w-auto" />
            </div>
            <h1 className="form-title">Link Expired</h1>
            <p style={{ textAlign: "center", fontSize: "0.875rem", color: "#6b7280", marginBottom: "1.25rem" }}>
              {tokenMessage}
            </p>
            <button
              onClick={() => navigate("/forgot-password")}
              className="submit-button"
              style={{ letterSpacing: "0.05em" }}
            >
              REQUEST NEW LINK
            </button>
            <p className="form-footer" style={{ marginTop: "0.75rem" }}>
              <Link className="toggle-form" to="/login" style={{ color: "#18181b", fontWeight: 600 }}>
                BACK TO SIGN IN
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Valid token — show password form
  return (
    <div className="auth-container">
      <div className="background-overlay"></div>
      <div className="form-container">
        <div className="form-card">
          <div className="atlas-logo">
            <img src={talentLogo2} alt="Talent Logo" className="h-14 w-auto" />
          </div>

          <h1 className="form-title">Set New Password</h1>
          <p style={{ textAlign: "center", fontSize: "0.875rem", color: "#6b7280", marginBottom: "1rem" }}>
            Enter a new password for your account.
          </p>

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">New Password*</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
              <span className="password-hint">Minimum 8 characters</span>
            </div>

            <button type="submit" className="submit-button" style={{ marginTop: "0.5rem", letterSpacing: "0.05em" }}>
              RESET PASSWORD
            </button>
          </form>

          <p className="form-footer" style={{ marginTop: "0.75rem" }}>
            <Link className="toggle-form" to="/login" style={{ color: "#18181b", fontWeight: 600 }}>
              BACK TO SIGN IN
            </Link>
          </p>

          <p className="form-footer">
            Don&apos;t have an account yet?{" "}
            <Link className="toggle-form" to="/signup">Sign up now</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
